/**
 * Reportes → **Diferencia diaria de combustibles** (`/reportes/diferencia-diaria`).
 *
 * Puerto de `Producto::diferencia_diaria` + `_preparar_datos_combustible_diario`:
 * cruza **día a día** lo que el contómetro del surtidor dice que salió de cada
 * tanque (`pts_pump_transactions`) con lo que entró por caja —visa, izipay,
 * transferencia, bóveda, gastos, ingresos adicionales, lo entregado al verificador
 * y las **ventas a crédito**— y deja a la vista el **pendiente** de cada día.
 *
 * Reglas conservadas:
 * - Sólo cuentan los turnos **cerrados** con `estado = 1`.
 * - `venta_total = combustible − gastos − descuentos − visa − transferencia +
 *   ingresos`, y `pendiente = venta_total − bóveda − entregado − crédito`.
 * - Los descuentos se separan en **contado** y **crédito** (los de
 *   `credito_venta`), como el original.
 * - Si el mes elegido es el actual, el reporte corta en el día de hoy.
 *
 * Diferencias anotadas: el mes se elige con un selector de mes (el original lo
 * recibía por `strtotime`) y el reporte añade los **totales del mes**, que allí
 * había que sumar a mano.
 */

import {
  ArrowLeft,
  CalendarRange,
  FileSpreadsheet,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { fechaCorta, monto } from '../gestion-formato';
import { productosApi, useDiferenciaDiaria } from '../productos.api';

/** Mes actual en formato `AAAA-MM`. */
function mesActual(): string {
  const ahora = new Date();

  return `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
}

export function DiferenciaDiariaPage() {
  useDocumentTitle('Reportes · Diferencia diaria de combustibles');

  const [mes, setMes] = useState(mesActual);
  const [error, setError] = useState<string | null>(null);
  const consulta = useDiferenciaDiaria(mes);

  /** Descarga el reporte en CSV (Excel), que necesita el token de la sesión. */
  const exportar = async () => {
    setError(null);

    try {
      await abrirPdfAutenticado(productosApi.exportarDiferenciaDiaria(mes), {
        nombre: `combustible-diario-${mes}.csv`,
      });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const dias = consulta.data?.dias ?? [];
  const totales = consulta.data?.totales;
  const productos = consulta.data?.productos ?? [];

  // Las dos tablas comparten los días, así que comparten también el paginado.
  const paginacion = usePaginacion(dias, { clave: mes });

  const contometroDe = (dia: (typeof dias)[number], producto: string) =>
    dia.contometro.find((fila) => fila.nombre === producto);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="text-2xl font-bold text-ink-900">Diferencia diaria de combustibles</h1>
          <p className="mt-1 text-sm text-ink-500">
            Contómetro del surtidor frente a la caja de cada día: visa, izipay, transferencia,
            bóveda, gastos, ingresos, entregado y ventas a crédito.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            label="Mes"
            type="month"
            containerClassName="w-40"
            value={mes}
            onChange={(evento) => setMes(evento.target.value || mesActual())}
          />
          <Button
            variant="outline"
            size="sm"
            leftIcon={<CalendarRange className="size-4" />}
            onClick={() => setMes(mesActual())}
          >
            Mes actual
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!dias.length}
            leftIcon={<FileSpreadsheet className="size-4" />}
            onClick={() => void exportar()}
          >
            Excel
          </Button>
        </div>
      </header>

      {error ? (
        <Alert variant="error" title="No se pudo exportar">
          {error}
        </Alert>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo preparar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {totales ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              icono: Wallet,
              etiqueta: 'Combustible del contómetro',
              valor: monto(totales.combustible),
              detalle: `${consulta.data?.fecha_inicio ?? ''} → ${consulta.data?.fecha_fin ?? ''}`,
              tono: 'bg-brand-50 text-brand-600',
            },
            {
              icono: TrendingUp,
              etiqueta: 'Venta del día',
              valor: monto(totales.venta_total),
              detalle: `Crédito ${monto(totales.credito)}`,
              tono: 'bg-emerald-50 text-emerald-600',
            },
            {
              icono: TrendingDown,
              etiqueta: 'Pendiente del mes',
              valor: monto(totales.pendiente),
              detalle: `Bóveda ${monto(totales.boveda)} · entregado ${monto(totales.entregado)}`,
              tono: totales.pendiente < 0 ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600',
            },
            {
              icono: Wallet,
              etiqueta: 'Descuentos',
              valor: monto(totales.descuento_contado + totales.descuento_credito),
              detalle: `contado ${monto(totales.descuento_contado)} · crédito ${monto(totales.descuento_credito)}`,
              tono: 'bg-ink-100 text-ink-600',
            },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="flex items-start gap-3 p-4">
                <span className={cn('rounded-full p-2', tarjeta.tono)}>
                  <tarjeta.icono className="size-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    {tarjeta.etiqueta}
                  </p>
                  <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                  <p className="line-clamp-2 text-xs text-ink-500">{tarjeta.detalle}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {!consulta.isLoading && dias.length === 0 && !consulta.isError ? (
        <Alert variant="info" title="Sin turnos en ese mes">
          <span className={ICONO_EN_ALERTA}>
            El reporte sólo mira turnos **cerrados**; si el mes no tiene ninguno, no hay diferencia
            que calcular.
          </span>
        </Alert>
      ) : null}

      {dias.length > 0 ? (
        <Card>
          <CardContent className="space-y-4 p-4">
            {/*
             * Dos tablas en lugar de una anchísima: el mismo día se lee a la
             * izquierda y cada tabla cabe en el ancho disponible, así que no hay
             * que desplazarse a la derecha ni siquiera con la barra lateral
             * desplegada. Los turnos cierran la primera columna y los importes
             * de cada producto van debajo de su volumen.
             */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink-800">Contómetros por producto</h3>
              <div className="max-h-[55vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Día y turnos</th>
                      {productos.map((producto) => (
                        <th key={producto} className="px-2 py-2 text-right">
                          {producto}
                          <span className="block text-[10px] font-normal">GLL / S/</span>
                        </th>
                      ))}
                      <th className="px-2 py-2 text-right whitespace-nowrap">Venta del día</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Pendiente</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginacion.visibles.map((dia) => (
                      <tr key={dia.dia} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td className="max-w-[13rem] px-2 py-2">
                          <p className="font-semibold whitespace-nowrap text-ink-900">
                            {fechaCorta(dia.dia)}
                          </p>
                          {dia.turnos.length === 0 ? (
                            <p className="text-xs text-ink-500">sin turnos cerrados</p>
                          ) : (
                            dia.turnos.map((turno) => (
                              <p
                                key={turno.id}
                                className="truncate text-xs text-ink-600"
                                title={turno.grifero ?? ''}
                              >
                                {turno.grifero ?? `Turno ${turno.id}`}
                              </p>
                            ))
                          )}
                        </td>
                        {productos.map((producto) => {
                          const fila = contometroDe(dia, producto);

                          return (
                            <td
                              key={`${dia.dia}-${producto}`}
                              className="px-2 py-2 text-right whitespace-nowrap"
                            >
                              {fila ? (
                                <>
                                  <p className="font-semibold">{fila.volumen.toFixed(3)}</p>
                                  <p className="text-xs text-ink-500">{monto(fila.importe)}</p>
                                </>
                              ) : (
                                <span className="text-ink-400">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(dia.venta_total)}
                          <span className="block text-xs font-normal text-ink-500">
                            contóm. {monto(dia.combustible)}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <Badge variant={Math.abs(dia.pendiente) < 0.5 ? 'success' : 'danger'}>
                            {monto(dia.pendiente)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  {totales ? (
                    <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                      <tr className="text-sm font-semibold text-ink-900">
                        <td className="px-2 py-2">Totales del mes</td>
                        {productos.map((producto) => {
                          const volumen = dias.reduce(
                            (suma, dia) => suma + (contometroDe(dia, producto)?.volumen ?? 0),
                            0,
                          );
                          const importe = dias.reduce(
                            (suma, dia) => suma + (contometroDe(dia, producto)?.importe ?? 0),
                            0,
                          );

                          return (
                            <td
                              key={`total-${producto}`}
                              className="px-2 py-2 text-right whitespace-nowrap"
                            >
                              <p>{volumen.toFixed(3)}</p>
                              <p className="text-xs font-normal text-ink-500">{monto(importe)}</p>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {monto(totales.venta_total)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {monto(totales.pendiente)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>

              <Paginador {...paginacion} etiqueta="días" />
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink-800">
                Cobros, movimientos y descuentos
              </h3>
              <div className="max-h-[55vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Día</th>
                      <th className="px-1.5 py-2 text-right">Visa</th>
                      <th className="px-1.5 py-2 text-right">Izipay</th>
                      <th className="px-1.5 py-2 text-right">Transfer.</th>
                      <th className="px-1.5 py-2 text-right whitespace-nowrap">Desc. contado</th>
                      <th className="px-1.5 py-2 text-right whitespace-nowrap">Desc. crédito</th>
                      <th className="px-1.5 py-2 text-right">Crédito</th>
                      <th className="px-1.5 py-2 text-right">Gastos</th>
                      <th className="px-1.5 py-2 text-right">Bóveda</th>
                      <th className="px-1.5 py-2 text-right">Entregado</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginacion.visibles.map((dia) => (
                      <tr key={dia.dia} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td
                          className="px-2 py-2 font-semibold whitespace-nowrap text-ink-900"
                          title={
                            dia.turnos.length
                              ? `Turnos: ${dia.turnos
                                  .map((turno) => turno.grifero ?? `Turno ${turno.id}`)
                                  .join(' · ')}`
                              : 'Sin turnos cerrados'
                          }
                        >
                          {fechaCorta(dia.dia)}
                          <span className="block text-[11px] font-normal text-ink-500">
                            {dia.turnos.length} turno{dia.turnos.length === 1 ? '' : 's'}
                          </span>
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">{monto(dia.visa)}</td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.izipay)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.transferencia)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.descuento_contado)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.descuento_credito)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.credito)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.gastos)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.boveda)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(dia.entregado)}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  {totales ? (
                    <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                      <tr className="text-sm font-semibold text-ink-900">
                        <td className="px-2 py-2">Totales del mes</td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.visa)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.izipay)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.transferencia)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.descuento_contado)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.descuento_credito)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.credito)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.gastos)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.boveda)}
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {monto(totales.entregado)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>

              <Paginador {...paginacion} etiqueta="días" />
            </div>

            <p className="text-xs text-ink-500">
              Los cobros, los movimientos y los descuentos van en su propia tabla para que no haya
              que desplazarse a la derecha. El volumen va en la unidad del producto (galones para
              los combustibles líquidos) y el
              importe es lo que marcó el contómetro. El <strong>pendiente</strong> es
              <code> venta del día − bóveda − entregado − crédito</code>: en verde cuando cierra a cero
              (menos de medio sol). El botón <strong>Excel</strong> descarga el mismo cuadro en CSV
              (separador <code>;</code> y punto decimal), que Excel abre y suma directamente.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
